import React, { useState } from "react";
import { X, Save, Truck, AlertCircle, Wallet } from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./add-customer-modal.css";

export default function AddVendorModal({ onClose, onAdd }) {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    company_name: "",
    phone: "",
    currency: "NGN",
    open_balance: "0",
    address: "",
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function save(e) {
    e?.preventDefault();
    setErr("");

    if (!companyId)
      return setErr("Missing company. Please reload and try again.");
    if (!form.name.trim()) return setErr("Vendor name is required.");

    setBusy(true);

    try {
      const payload = {
        company_id: companyId,
        name: form.name.trim(),
        company_name: form.company_name?.trim() || null,
        phone: form.phone?.trim() || null,
        currency: form.currency || "NGN",
        open_balance: Number(form.open_balance || 0),
        address: form.address?.trim() || null,
      };

      const { data, error } = await supabase
        .from("vendors")
        .insert(payload)
        .select("*")
        .single();

      if (error) throw error;

      onAdd?.(data);
      onClose?.();
    } catch (e) {
      console.error("AddVendorModal save error:", e);
      setErr(e.message || "Failed to add vendor.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="acm-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="acm-modal" onClick={(e) => e.stopPropagation()}>
        <header className="acm-head">
          <div className="acm-title-wrap">
            <div className="acm-icon">
              <Truck size={20} />
            </div>

            <div>
              <p className="acm-eyebrow">Vendor Setup</p>
              <h2>Add New Vendor</h2>
              <p>
                Create a vendor profile for purchases, bills, payments and
                payables.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="acm-close"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            <X size={18} />
          </button>
        </header>

        {err && (
          <div className="acm-banner error">
            <AlertCircle size={16} />
            {err}
          </div>
        )}

        <form onSubmit={save} className="acm-form">
          <section className="acm-section">
            <h3>Basic Information</h3>

            <label>
              Vendor Name
              <input
                type="text"
                placeholder="ABC Supplies"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                required
                disabled={busy}
              />
            </label>

            <label>
              Company Name
              <input
                type="text"
                placeholder="ABC Supplies Limited"
                value={form.company_name}
                onChange={(e) => setField("company_name", e.target.value)}
                disabled={busy}
              />
            </label>

            <label>
              Address
              <input
                type="text"
                placeholder="12 Admiralty Way, Lekki Phase 1, Lagos"
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                disabled={busy}
              />
            </label>
          </section>

          <section className="acm-section">
            <h3>Contact & Opening Balance</h3>

            <div className="acm-grid three">
              <label>
                Phone
                <input
                  type="text"
                  placeholder="+234 801 234 5678"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  disabled={busy}
                />
              </label>

              <label>
                Currency
                <select
                  value={form.currency}
                  onChange={(e) => setField("currency", e.target.value)}
                  disabled={busy}
                >
                  <option value="NGN">NGN</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </label>

              <label>
                Opening Balance
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.open_balance}
                  onChange={(e) => setField("open_balance", e.target.value)}
                  disabled={busy}
                />
              </label>
            </div>

            <div className="acm-help">
              <Wallet size={16} />
              Opening balance means the amount your business already owed this
              vendor before you started using KostPadi.
            </div>
          </section>

          <div className="acm-actions">
            <button
              type="button"
              className="acm-btn ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>

            <button type="submit" className="acm-btn primary" disabled={busy}>
              <Save size={16} />
              {busy ? "Saving Vendor…" : "Save Vendor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
